/**
 * Minimal theme provider. Writes the `dark` class onto <html> so the Tailwind
 * `darkMode: ["class"]` tokens in index.css apply, and remembers the choice in
 * localStorage.
 */
import { createContext, useContext, useEffect, useState } from "react";

type Theme = "dark" | "light" | "system";

interface ThemeProviderState {
  theme: Theme;
  resolvedTheme: "dark" | "light";
  setTheme: (theme: Theme) => void;
}

const ThemeProviderContext = createContext<ThemeProviderState>({
  theme: "dark",
  resolvedTheme: "dark",
  setTheme: () => {},
});

function systemTheme(): "dark" | "light" {
  return window.matchMedia("(prefers-color-scheme: dark)").matches
    ? "dark"
    : "light";
}

export function ThemeProvider({
  children,
  defaultTheme = "dark",
  storageKey = "dashbase-theme",
}: {
  children: React.ReactNode;
  defaultTheme?: Theme;
  storageKey?: string;
}) {
  const [theme, setThemeState] = useState<Theme>(() => {
    // Private windows and blocked site data make this throw, not return null.
    try {
      return (localStorage.getItem(storageKey) as Theme) || defaultTheme;
    } catch {
      return defaultTheme;
    }
  });

  const [resolved, setResolved] = useState<"dark" | "light">(() =>
    defaultTheme === "system" ? "dark" : (defaultTheme as "dark" | "light"),
  );

  useEffect(() => {
    const root = window.document.documentElement;
    const applied = theme === "system" ? systemTheme() : theme;

    root.classList.remove("light", "dark");
    root.classList.add(applied);
    setResolved(applied);

    if (theme !== "system") return;

    // Follow the OS while the user is on "system".
    const media = window.matchMedia("(prefers-color-scheme: dark)");
    const onChange = () => {
      const next = systemTheme();
      root.classList.remove("light", "dark");
      root.classList.add(next);
      setResolved(next);
    };
    media.addEventListener("change", onChange);
    return () => media.removeEventListener("change", onChange);
  }, [theme]);

  const setTheme = (next: Theme) => {
    try {
      localStorage.setItem(storageKey, next);
    } catch {
      // Persisting is a convenience; the theme still applies for this session.
    }
    setThemeState(next);
  };

  return (
    <ThemeProviderContext.Provider
      value={{ theme, resolvedTheme: resolved, setTheme }}
    >
      {children}
    </ThemeProviderContext.Provider>
  );
}

export const useTheme = () => useContext(ThemeProviderContext);
